/**
 * The quotes/projects/CRM package's starter suggestions (P4): on an open
 * quote, the quote list, a project without an open quote, a CRM company and
 * the document settings page — each only what this package's tools
 * (`get_quote`, `search_quotes`, `prepare_quote_email` /
 * `send_quote_email`, `create_quote`, `list_project_documents`, the CRM
 * and settings tools) carry out there.
 */
import type { PageContext } from "../pageContext.js";
import { defineAgentStarter, onRoutes } from "./agentStarters.js";

const onQuote = (context: PageContext) => context.quoteId !== undefined;

const onProjectWithoutQuote = (context: PageContext) =>
  context.projectId !== undefined && context.quoteId === undefined;

const onCrmCompany = (context: PageContext) =>
  context.crmCompanyId !== undefined;

export const quotesProjectsCrmAgentStarters = [
  defineAgentStarter({
    id: "quotes.missing-prices",
    ns: "Assistant",
    textKey: "Check this quote for missing prices",
    order: 10,
    match: onQuote,
    requiredCapabilities: ["quoting"],
  }),
  defineAgentStarter({
    id: "quotes.open-points",
    ns: "Assistant",
    textKey: "What is still open in this quote?",
    order: 20,
    match: onQuote,
    requiredCapabilities: ["quoting"],
  }),
  defineAgentStarter({
    id: "quotes.send-email",
    ns: "Assistant",
    textKey: "Send this quote to the customer by e-mail",
    order: 30,
    match: onQuote,
    requiredCapabilities: ["quoting"],
  }),
  defineAgentStarter({
    id: "quotes.answer-wizard-questions",
    ns: "Assistant",
    textKey: "Answer the open questions of this quote",
    order: 40,
    match: onQuote,
    requiredCapabilities: ["quoting"],
  }),
  defineAgentStarter({
    id: "quotes.large-quotes",
    ns: "Assistant",
    textKey: "Show me all quotes from the last 10 days over €10,000 in Berlin",
    order: 10,
    match: onRoutes("/documents/quotes"),
    requiredCapabilities: ["quoting"],
  }),
  defineAgentStarter({
    id: "projects.new-quote",
    ns: "Assistant",
    textKey: "Create a quote for this project",
    order: 10,
    match: onProjectWithoutQuote,
    requiredCapabilities: ["quoting"],
  }),
  defineAgentStarter({
    // No requiredCapabilities: list_project_documents checks invoicing,
    // ordering or timeTracking per document section (any-of), and
    // requiredCapabilities can only express all-of.
    id: "projects.documents",
    ns: "Assistant",
    textKey: "Which documents does this project have?",
    order: 20,
    match: onProjectWithoutQuote,
  }),
  defineAgentStarter({
    id: "crm.company-summary",
    ns: "Assistant",
    textKey: "Summarise this company with its contacts",
    order: 10,
    match: onCrmCompany,
  }),
  defineAgentStarter({
    id: "crm.duplicates",
    ns: "Assistant",
    textKey: "Check this company for duplicates",
    order: 20,
    match: onCrmCompany,
  }),
  defineAgentStarter({
    id: "settings.opening-text",
    ns: "Assistant",
    textKey: "Show and change our quote opening text",
    order: 10,
    match: onRoutes("/organisation/settings/documents"),
    requiredCapabilities: ["advancedSettings"],
  }),
];
